import { useEffect, useState } from 'react';
import RuleActionGroup from './RuleActionGroup';
import { RuleApiModel, SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import { P2GGrid } from '@parcel2go/core-ui';
import { allowInlineRule } from './allowInlineRule';
import { RuleNames } from '../../rules/RuleGenerator';
import { toConditionDisplayName } from './toConditionDisplayName';
import { toActionDisplayName } from './toActionDisplayName';
import { useSmartSendApi } from '~/lib/api/smartsend/use-smartsend-api';
import { useAnalytics } from '@parcel2go/npm-p2g-app';
import { GenerateBaseAnalyticsPayload } from '~/lib/utils/analytics';

interface props {
  item: SmartSendItemApiModel;
  actionGroups: string[][];
  loader?: React.ReactNode;
  limitToSelection: boolean;
  sections: string[];
}

function generateInlineRuleGroupKey(actions: string[]) {
  return actions.join('-');
}

export default function RuleSummary(props: props) {
  const { createRule, deleteRule, getRules, updateRule } = useSmartSendApi();
  const [rules, setRules] = useState<RuleApiModel[]>(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState('');
  const { track } = useAnalytics();

  const pushRuleChangesLogs = (event: string, values: string[]) => {
    values?.forEach((condition) => {
      track({
        type: event,
        payload: {
          ...GenerateBaseAnalyticsPayload([...props.sections]),
          condition: condition
        }
      });
    });
  };

  useEffect(() => {
    const load = async () => {
      const r = await getRules('SmartSend');
      setRules(r.success ? r.result.Rules : null);
      setLoading(false);
    };
    load();
    track({
      type: 'rule_summary_loaded',
      payload: {
        ...GenerateBaseAnalyticsPayload([...props.sections])
      }
    });
  }, []);

  const addNewRule = async (r: RuleApiModel) => {
    logRuleChanges(r, 'add');
    const res = await createRule(r);
    if (res.success) {
      setRules([...rules, res.result.Rule]);
      setActive('');
    }
  };

  const logRuleChanges = (r: RuleApiModel, action: 'add' | 'delete' | 'modify') => {
    const existing = rules.find((x) => x.Id === r.Id);

    const addActions = r.Actions.filter((x) => !existing?.Actions.map((y) => y.Field).includes(x.Field)).map(
      (x) => x.Field
    );
    const addConditions = r.Conditions.filter((x) => !existing?.Conditions.map((y) => y.Field).includes(x.Field)).map(
      (x) => x.Field
    );
    let removeActions = existing?.Actions.filter((x) => !r.Actions.map((y) => y.Field).includes(x.Field)).map(
      (x) => x.Field
    );
    const removeConditions = existing?.Conditions.filter(
      (x) => !r.Conditions.map((y) => y.Field).includes(x.Field)
    ).map((x) => x.Field);

    if (action === 'delete') {
      removeActions = existing?.Actions.map((x) => x.Field);
    }

    pushRuleChangesLogs('delete_condition', removeConditions?.map(toConditionDisplayName));
    pushRuleChangesLogs('delete_action', removeActions?.map(toActionDisplayName));

    pushRuleChangesLogs('add_condition', addConditions?.map(toConditionDisplayName));
    pushRuleChangesLogs('add_action', addActions?.map(toActionDisplayName));
  };

  const deleteExistingRule = async (r: RuleApiModel) => {
    logRuleChanges(r, 'delete');
    const res = await deleteRule(r.Id, r.RuleType);
    if (res.success) {
      setRules(rules.filter((x) => x.Id !== r.Id));
      setActive('');
    }
  };

  const modifyRule = async (r: RuleApiModel) => {
    logRuleChanges(r, 'modify');
    const res = await updateRule(r);
    if (res.success) {
      setRules([...rules.filter((x) => x.Id !== r.Id), r]);
      setActive('');
    }
  };

  const onSave = async (r: RuleApiModel) => {
    if (r.Conditions.length === 0 && r.Tags.includes(RuleNames.InlineTag)) {
      deleteExistingRule(r);
    } else {
      modifyRule(r);
    }
  };

  const actionGroups = props.actionGroups
    .filter((g) => allowInlineRule(props.item, g))
    .filter((x) => !props.limitToSelection || !active || generateInlineRuleGroupKey(x) === active);

  if (loading && props.loader) {
    return <>{props.loader}</>;
  }

  if (rules == null || actionGroups.length === 0) {
    return null;
  }

  return (
    <P2GGrid container direction={'column'} spacing={1}>
      {actionGroups.map((g) => {
        const id = generateInlineRuleGroupKey(g);
        return (
          <P2GGrid item key={id}>
            <RuleActionGroup
              toggle={(open) => setActive(open ? id : '')}
              rules={rules}
              actions={g}
              item={props.item}
              addNewRule={addNewRule}
              updateExistingRule={onSave}
            />
          </P2GGrid>
        );
      })}
    </P2GGrid>
  );
}
